import React, { useState } from 'react';
import { carWashData } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Phone, Mail, MapPin, Clock, Star, Droplets, Sparkles, Shield, Award } from 'lucide-react';

const Home = () => {
  const [activeService, setActiveService] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_shine-station-3/artifacts/t37ax4ki_Logo_Trushine.png" 
              alt="Trushine Logo" 
              className="h-24 w-auto"
            />
          </div>
          <nav className="hidden md:flex gap-8">
            <a href="#services" className="text-slate-700 hover:text-slate-900 font-medium transition-colors">Servicios</a>
            <a href="#about" className="text-slate-700 hover:text-slate-900 font-medium transition-colors">Nosotros</a>
            <a href="/lista-de-precios" className="text-slate-700 hover:text-slate-900 font-medium transition-colors">Lista de Precios</a>
            <a href="#contact" className="text-slate-700 hover:text-slate-900 font-medium transition-colors">Contacto</a>
          </nav>
          <a href="https://wa.me/573044175754" target="_blank" rel="noopener noreferrer">
            <Button className="bg-slate-900 hover:bg-slate-800 text-white">Reservar Ahora</Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 z-0">
          <img 
            src={carWashData.hero.backgroundImage} 
            alt="Luxury Car" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-amber-600 hover:bg-amber-700 text-white border-0">
              <Sparkles className="w-4 h-4 mr-1" />
              Servicio Premium
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {carWashData.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 mb-4 font-light">
              {carWashData.hero.subtitle}
            </p>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl">
              {carWashData.hero.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="https://wa.me/573044175754" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">
                  {carWashData.hero.cta}
                </Button>
              </a>
              <a href="#services">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8 py-6">
                  Conocer Más
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Floating Stats */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 hidden lg:block">
          <div className="flex gap-8 bg-white/10 backdrop-blur-md rounded-2xl px-8 py-4 border border-white/20">
            {carWashData.aboutUs.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-white">{stat.number}</div>
                <div className="text-sm text-slate-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-slate-200 text-slate-900 hover:bg-slate-300">
              Nuestros Servicios
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Cuidado Excepcional para Su Vehículo
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Desde lavados rápidos hasta detallado completo, ofrecemos servicios adaptados a sus necesidades
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {carWashData.services.map((service) => (
              <Card 
                key={service.id} 
                className={`group hover:shadow-2xl transition-all duration-300 border-2 overflow-hidden ${
                  service.featured ? 'border-amber-600 bg-gradient-to-br from-amber-50 to-white' : 'border-slate-200 hover:border-slate-300'
                }`}
                onMouseEnter={() => setActiveService(service.id)}
                onMouseLeave={() => setActiveService(null)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  {service.featured && (
                    <Badge className="absolute top-4 right-4 bg-amber-600 text-white">
                      <Award className="w-4 h-4 mr-1" />
                      Destacado
                    </Badge>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-4">
                    <div className="flex items-center gap-2 text-white">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{service.duration}</span>
                    </div>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900">{service.title}</CardTitle>
                  <CardDescription className="text-slate-600 text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-700">
                        <span className="text-amber-600 mt-1">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="https://wa.me/573044175754" target="_blank" rel="noopener noreferrer" className="block">
                    <Button 
                      className={`w-full mt-6 ${
                        service.featured 
                          ? 'bg-amber-600 hover:bg-amber-700 text-white' 
                          : 'bg-slate-900 hover:bg-slate-800 text-white'
                      }`}
                    >
                      Reservar Este Servicio
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-slate-200 text-slate-900 hover:bg-slate-300">
                {carWashData.aboutUs.title}
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                {carWashData.aboutUs.subtitle}
              </h2>
              <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                {carWashData.aboutUs.description}
              </p>
              <p className="text-lg text-slate-700 mb-8 leading-relaxed">
                {carWashData.aboutUs.mission}
              </p>

              {/* Core Values Accordion */}
              <Accordion type="single" collapsible className="w-full">
                {carWashData.aboutUs.values.map((value, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-slate-300">
                    <AccordionTrigger className="text-slate-900 hover:text-amber-600 font-semibold">
                      {value.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-700">
                      {value.description}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={carWashData.aboutUs.image} 
                  alt="Professional Team" 
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
              </div>
              
              {/* Stats Overlay */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="w-8 h-8 text-amber-600" />
                  <div>
                    <div className="text-3xl font-bold text-slate-900">100%</div>
                    <div className="text-sm text-slate-600">Garantía de Satisfacción</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-slate-200 text-slate-900 hover:bg-slate-300">
              Testimonios
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Lo Que Dicen Nuestros Clientes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {carWashData.testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-slate-200 hover:border-amber-600 transition-colors">
                <CardHeader>
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <CardTitle className="text-slate-900">{testimonial.name}</CardTitle>
                  <CardDescription className="text-slate-600">{testimonial.vehicle}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 italic">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {carWashData.contact.title}
            </h2>
            <p className="text-xl text-slate-300 mb-12">
              {carWashData.contact.subtitle}
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-slate-800 border-slate-700 text-left">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Phone className="w-5 h-5 text-amber-600" />
                    Teléfono
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300 text-lg">{carWashData.contact.phone}</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700 text-left">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Mail className="w-5 h-5 text-amber-600" />
                    Correo Electrónico
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300 text-lg">{carWashData.contact.email}</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700 text-left">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <MapPin className="w-5 h-5 text-amber-600" />
                    Dirección
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300">{carWashData.contact.address}</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700 text-left">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Clock className="w-5 h-5 text-amber-600" />
                    Horario
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <p className="text-slate-300">{carWashData.contact.hours.weekdays}</p>
                  {carWashData.contact.hours.saturday && (
                    <p className="text-slate-300">{carWashData.contact.hours.saturday}</p>
                  )}
                  {carWashData.contact.hours.sunday && (
                    <p className="text-slate-300">{carWashData.contact.hours.sunday}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <a href="https://wa.me/573044175754" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">
                Programar Su Cita
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_shine-station-3/artifacts/t37ax4ki_Logo_Trushine.png" 
                  alt="Trushine Logo" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-slate-400">
                Servicios premium de detallado y lavado de autos en los que puede confiar.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Enlaces Rápidos</h3>
              <ul className="space-y-2">
                <li><a href="#services" className="hover:text-amber-600 transition-colors">Servicios</a></li>
                <li><a href="#about" className="hover:text-amber-600 transition-colors">Nosotros</a></li>
                <li><a href="/lista-de-precios" className="hover:text-amber-600 transition-colors">Lista de Precios</a></li>
                <li><a href="#contact" className="hover:text-amber-600 transition-colors">Contacto</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Información de Contacto</h3>
              <ul className="space-y-2">
                <li>{carWashData.contact.phone}</li>
                <li>{carWashData.contact.email}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p>&copy; 2025 Trushine Car Detailing. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
